import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Avatar, Box, Chip, LinearProgress, Pagination, Paper, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Typography, useTheme, alpha } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FerramentasDaListagemCadastros, CartaoFlutuanteCarregando } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { getRowsPerPageCache, setRowsPerPageCache } from '../../../shared/utils/paginaRowsCache';
import InventoryIcon from '@mui/icons-material/Inventory';
import NumbersIcon from '@mui/icons-material/Numbers';
import TagIcon from '@mui/icons-material/Tag';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { IListagemDeProdutos, ProdutosService } from '../../../shared/services/api/estoque/produtos/ProdutosService';
import { Image as ImageIcon, Description as DescriptionIcon, QrCode as QrCodeIcon } from '@mui/icons-material';

export const ListagemDeProdutos: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestIdRef = useRef(0);
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [rows, setRows] = useState<IListagemDeProdutos[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'produtos';
  const [rowsPerPage, setRowsPerPage] = useState(() => getRowsPerPageCache(ROWS_CACHE_KEY) ?? Environment.LIMITE_DE_LINHASSMALL);
  const [containerHeight, setContainerHeight] = useState<number>();
  const tabelaRef = useRef<HTMLDivElement>(null);

  // Altura fixa de uma linha de produto: avatar (32px) + padding vertical (py: 0.6 => 9.6px) +
  // borda. As colunas usam noWrap e o avatar tem tamanho fixo, então a altura real nunca varia
  // com o conteúdo — por isso pode ser uma constante em vez de medida no DOM. Medir uma linha
  // real exigiria que os produtos já tivessem chegado da API, obrigando uma primeira requisição
  // "de calibração" com um limite ainda errado e uma segunda já corrigida.
  const ALTURA_LINHA_PADRAO = 43;

  const calcularLimite = () => {
    const container = tabelaRef.current;
    if (!container) return;

    // Mede o espaço real até o rodapé da janela em vez de usar um valor fixo de "calc(100vh - Npx)",
    // que ficava desalinhado com a altura real do título/toolbar e causava scroll na página inteira.
    const BOTTOM_GAP = 16;
    const alturaContainer = Math.max(200, window.innerHeight - container.getBoundingClientRect().top - BOTTOM_GAP);

    // O <thead> é sempre renderizado, mesmo sem produtos carregados, então dá para medi-lo
    // logo no mount — diferente da linha do corpo, ele não depende de dados terem chegado.
    const headerEl = container.querySelector('thead');
    const alturaHeader = headerEl ? headerEl.getBoundingClientRect().height : 34;

    // Reservada como constante, nunca medida a partir do <tfoot>: a paginação só existe no
    // DOM quando totalCount > rowsPerPage, então medi-la criaria uma dependência circular
    // (ela aparece/desaparece conforme o próprio rowsPerPage muda).
    const FOOTER_RESERVADO = 56;
    const MIN_ROWS = 1;
    const MAX_ROWS = 100;
    const FOLGA_LINHAS = 1;
    const alturaDisponivel = alturaContainer - alturaHeader - FOOTER_RESERVADO;
    const novoLimite = Math.min(
      MAX_ROWS,
      Math.max(MIN_ROWS, Math.floor(alturaDisponivel / ALTURA_LINHA_PADRAO) - FOLGA_LINHAS)
    );

    setContainerHeight(prev => (prev === alturaContainer ? prev : alturaContainer));
    setRowsPerPageCache(ROWS_CACHE_KEY, novoLimite);
    setRowsPerPage(prev => (prev === novoLimite ? prev : novoLimite));
  };

  // useLayoutEffect (não useEffect) para rodar de forma síncrona antes da pintura e, mais
  // importante, antes do efeito de busca (que depende de rowsPerPage) disparar a requisição
  // inicial — assim a primeira busca já sai com o limite correto, sem uma segunda requisição
  // de correção.
  useLayoutEffect(() => {
    calcularLimite();
  }, []);

  useEffect(() => {
    window.addEventListener('resize', calcularLimite);
    return () => window.removeEventListener('resize', calcularLimite);
  }, []);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };

  const tipo = useMemo(() => {
    return searchParams.get('tipo') || 'descricao';
  }, [searchParams]);

  const filter = useMemo(() => {
    return searchParams.get('filter') || '';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    debounce(() => {
      // Só zera a listagem quando a busca "debounced" realmente vai disparar (não a cada
      // tecla digitada), e sempre imediatamente antes do fetch — assim nunca fica visível
      // uma mistura de itens da pesquisa anterior com o resultado da pesquisa atual: ou
      // mostra o resultado antigo completo (enquanto o usuário ainda digita) ou mostra
      // "carregando" e depois só o resultado mais recente.
      if (requestId !== requestIdRef.current) return;
      setRows([]);
      setTotalCount(0);

      ProdutosService.getAll(pagina, filter, tipo, rowsPerPage)
        .then((result) => {
          if (requestId !== requestIdRef.current) return;
          setIsLoading(false);
          if (result instanceof Error) {
            setRows([]);
            setTotalCount(0);
            setMsgType('error');
            setMsg(result.message);
            setOpenMsg(true);
            return;
          }
          if (!result.data || result.data.length === 0) {
            setRows([]);
            setTotalCount(0);
            return;
          }
          setRows(result.data);
          setTotalCount(result.totalCount);
        });
    });
  }, [filter, pagina, tipo, debounce, rowsPerPage]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({ filter, tipo, pagina: '1' }, { replace: true });
    }
  }, [rowsPerPage]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'F8') {
        event.preventDefault();
  
        const tipos = [
          'descricao',
          'referencia',
          'codinterno',
          'codbarra',
        ];
  
        const indiceAtual = tipos.indexOf(tipo);
        const proximoIndice =
          indiceAtual === tipos.length - 1 ? 0 : indiceAtual + 1;
  
        setSearchParams(
          {
            filter,
            tipo: tipos[proximoIndice],
            pagina: '1',
          },
          { replace: true }
        );
      }
    };
  
    window.addEventListener('keydown', handleKeyDown);
  
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [tipo, filter, setSearchParams]);

  return (
    <>
      <LayoutBaseDePagina
        titulo='Listagem de Produtos'
        barraDeFerramentas={
          <FerramentasDaListagemCadastros
            mostrarInputBusca
            mostrarInputBasicSelect
            textoDaBusca={filter}
            textoDoSelect={tipo}
            textoBotaoNovo='Novo Produto'
            aoClicarEmNovo={() => navigate('/produto/novo', { state: { from: `${location.pathname}${location.search}` } })}
            aoMudarTextoDeBusca={texto => setSearchParams({ filter: texto, tipo: tipo, pagina: '1' }, { replace: true })}
            aoMudarTextoDoSelect={textoselect => setSearchParams({ filter: filter, tipo: textoselect, pagina: '1' }, { replace: true })}
            dadosSelecao={[
              { nameBD: 'descricao', descricao: 'Descrição (F8)' },
              { nameBD: 'referencia', descricao: 'Referência(F8)' },
              { nameBD: 'codinterno', descricao: 'Código Interno(F8)' },
              { nameBD: 'codbarra', descricao: 'Código de Ean(F8)' },
            ]}
          />
        }
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openMsg}
          autoHideDuration={6000}
          onClose={handleCloseMsg}
        >
          <Alert
            onClose={handleCloseMsg}
            severity={msgType}
            variant='filled'
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            borderRadius: 1,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            backgroundColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.9)
              : theme.palette.background.paper,
          }}
        >
          <CartaoFlutuanteCarregando
            open={isLoading}
            message="Carregando produtos..."
          />
          <TableContainer
            ref={tabelaRef}
            sx={{
              // 'auto' quando a página atual não preenche rowsPerPage (única página curta ou
              // última página parcial de uma listagem paginada), para o fundo acompanhar a
              // quantidade real de itens em vez de esticar até o fim da tela. Durante o
              // carregamento, porém, a lista é zerada (ver comentário no efeito de busca) — sem
              // manter a altura cheia aqui, o container encolhe pro tamanho só do cabeçalho e o
              // cartão de loading fica espremido lá em cima em vez de centralizado.
              height: (isLoading || rows.length >= rowsPerPage)
                ? (containerHeight ? `${containerHeight}px` : 'calc(100vh - 160px)')
                : 'auto',
              maxHeight: containerHeight ? `${containerHeight}px` : 'calc(100vh - 160px)',
              overflow: 'auto',
              scrollbarGutter: 'stable',
            }}
          >
            <Table size="small" sx={{ minWidth: '100%' }}>
              <TableHead>
                <TableRow sx={{
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.dark, 0.3)
                    : alpha(theme.palette.primary.main, 0.06),
                  '& th': {
                    fontWeight: 'bold',
                    color: theme.palette.mode === 'dark'
                      ? theme.palette.primary.light
                      : theme.palette.primary.dark,
                    fontSize: '0.7rem',
                    py: 0.75,
                    px: 0.6,
                    borderBottom: `1px solid ${theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.light, 0.15)
                      : alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}>
                  <TableCell sx={{ width: '55px' }}>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <ImageIcon fontSize="small" sx={{ fontSize: '14px' }} />
                      <span>Img</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '115px' }}>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <QrCodeIcon fontSize="small" sx={{ fontSize: '14px' }} />
                      <span>EAN</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '85px' }}>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <NumbersIcon fontSize="small" sx={{ fontSize: '14px' }} />
                      <span>Cód. Int</span>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <DescriptionIcon fontSize="small" sx={{ fontSize: '14px' }} />
                      <span>Descrição</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '60px' }}>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <InventoryIcon fontSize="small" sx={{ fontSize: '14px' }} />
                      <span>UN</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '60px' }}>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <NumbersIcon fontSize="small" sx={{ fontSize: '14px' }} />
                      <span>QTD</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '85px' }}>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <BrandingWatermarkIcon fontSize="small" sx={{ fontSize: '14px' }} />
                      <span>Marca</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '90px' }}>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <TagIcon fontSize="small" sx={{ fontSize: '14px' }} />
                      <span>Referência</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '60px' }}>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <TagIcon fontSize="small" sx={{ fontSize: '14px' }} />
                      <span>Tipo</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '60px' }}>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <LocationOnIcon fontSize="small" sx={{ fontSize: '14px' }} />
                      <span>Local</span>
                    </Box>
                  </TableCell>
                  <TableCell align='right' sx={{ width: '60px' }}>
                    <Box display="flex" alignItems="center" gap={0.3} justifyContent="flex-end">
                      <AttachMoneyIcon fontSize="small" sx={{ fontSize: '14px' }} />
                      <span>Valor</span>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={row.idproduto}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.15s ease-in-out',
                      backgroundColor: theme.palette.mode === 'dark'
                        ? (index % 2 === 0
                          ? alpha(theme.palette.background.paper, 0.4)
                          : alpha(theme.palette.background.paper, 0.6))
                        : (index % 2 === 0
                          ? 'transparent'
                          : alpha(theme.palette.grey[50], 0.3)),
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.primary.main, 0.12)
                          : alpha(theme.palette.primary.main, 0.03),
                      },
                      '&:last-child td': {
                        borderBottom: 0,
                      },
                      '& td': {
                        py: 0.6,
                        px: 0.6,
                        fontSize: '0.7rem',
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                      }
                    }}
                    onClick={() => navigate(`/produto/${row.idproduto}`, { state: { from: `${location.pathname}${location.search}` } })}
                  >
                    <TableCell>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          margin: 'auto',
                          bgcolor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.grey[700], 0.5)
                            : theme.palette.grey[200],
                          fontSize: '16px'
                        }}
                        alt={row.descricao}
                        src={process.env.REACT_APP_URL_IMGPRODUTOS + row.anp}
                      >
                        {!row.anp && <ImageIcon sx={{ fontSize: '14px' }} />}
                      </Avatar>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" color="secondary.main"
                        fontWeight="bold" fontSize="0.68rem">
                        {row.ean || 'Não informado'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: '"Roboto Mono", "Consolas", monospace',
                          fontSize: '0.90rem',
                          fontWeight: 600,
                          letterSpacing: '0.5px',
                        }}
                      >
                        {row.codinterno || '---'}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ maxWidth: '260px' }}>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color="text.primary"
                          fontSize="0.8rem"
                          noWrap
                        >
                          {row.descricao}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" color="text.primary" fontSize="0.7rem">
                          {row.un}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align='left'>
                      <Box textAlign="left">
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="success.main"
                          sx={{
                            fontSize: '0.7rem',
                            letterSpacing: '0.2px'
                          }}
                        >
                          {(row.quan ?? 0).toLocaleString('pt-BR', {})}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontSize="0.58rem">
                          Estoque
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.marca || 'Sem Marca'}
                        size="small"
                        color="primary"
                        variant={theme.palette.mode === 'dark' ? 'filled' : 'outlined'}
                        sx={{
                          fontWeight: 'medium',
                          height: '18px',
                          fontSize: '0.62rem',
                          '& .MuiChip-label': {
                            px: 0.6,
                            py: 0
                          },
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.dark, 0.25)
                            : 'transparent'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" color="text.primary" fontSize="0.7rem" noWrap>
                        {row.referencia}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.original ? 'Original' : 'Similar'}
                        size="small"
                        color={row.original ? 'success' : 'warning'}
                        variant={theme.palette.mode === 'dark' ? 'filled' : 'outlined'}
                        sx={{
                          fontWeight: 'bold',
                          height: '18px',
                          fontSize: '0.62rem',
                          '& .MuiChip-label': {
                            px: 0.6,
                            py: 0
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align='left'>
                      <Typography variant="body2" fontWeight="medium" color="text.primary" fontSize="0.7rem" noWrap>
                        {row.localizado}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <Box textAlign="right">
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="success.main"
                          sx={{
                            fontSize: '0.85rem',
                            letterSpacing: '0.2px'
                          }}
                        >
                          {(row.pvenda ?? 0).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                          })}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {rows.length === 0 && !isLoading && (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                      <Box>
                        <ImageIcon sx={{
                          fontSize: 35,
                          color: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.text.secondary, 0.4)
                            : 'text.secondary',
                          mb: 1
                        }} />
                        <Typography variant='h6' color='text.secondary' gutterBottom fontSize="0.85rem">
                          {Environment.LISTAGEM_VAZIA}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' fontSize="0.7rem">
                          {filter ? 'Tente ajustar os filtros de busca' : 'Nenhum produto encontrado'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}

              <TableFooter>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ p: 0 }}>
                      <LinearProgress
                        sx={{
                          height: '1.5px',
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.dark, 0.2)
                            : undefined,
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? theme.palette.primary.light
                              : undefined
                          }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                )}
                {totalCount > rowsPerPage && (
                  <TableRow>
                    <TableCell colSpan={11} sx={{
                      py: 1,
                      px: 0,
                      borderTop: `1px solid ${theme.palette.mode === 'dark'
                        ? alpha(theme.palette.divider, 0.2)
                        : alpha(theme.palette.divider, 0.1)}`,
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.default, 0.7)
                        : alpha(theme.palette.grey[50], 0.2)
                    }}>
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="100%"
                        sx={{
                          overflowX: 'auto',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'inline-flex',
                            margin: '0 auto',
                          }}
                        >
                          <Pagination
                            page={pagina}
                            count={Math.ceil(totalCount / rowsPerPage)}
                            onChange={(_, newPage) => setSearchParams({
                              filter,
                              tipo,
                              pagina: newPage.toString()
                            }, { replace: true })}
                            color="primary"
                            size="small"
                            shape="rounded"
                            showFirstButton
                            showLastButton
                            siblingCount={1}
                            boundaryCount={1}
                            sx={{
                              '& .MuiPaginationItem-root': {
                                fontWeight: 'bold',
                                color: theme.palette.text.primary,
                                fontSize: '0.7rem',
                                minWidth: '26px',
                                height: '26px',
                                backgroundColor: theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.background.paper, 0.7)
                                  : undefined,
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.primary.main, 0.25)
                                    : undefined
                                },
                                '&.Mui-selected': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.primary.main, 0.4)
                                    : undefined,
                                  color: theme.palette.mode === 'dark'
                                    ? theme.palette.common.white
                                    : undefined
                                }
                              }
                            }}
                          />
                        </Box>
                      </Box>
                      <Box textAlign="center" mt={0.5}>
                        <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                          Total de {totalCount} produto{totalCount !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableFooter>
            </Table>
          </TableContainer>
        </Paper>
      </LayoutBaseDePagina>
    </>
  );
};